/**
 * Session Engine v3 — EXPLORATORY / non-Canon prototype.
 *
 * The per-step run configuration (SPEC-00 §3): (1) goal framing, (2) tool
 * selection from the full registry (recommended first, rest available), (3) run
 * mode + execution controls.
 *
 * Placement: always rendered BELOW the step's content editor, not above it.
 * Tool picker shows ALL session-compatible tools. Step-specific recommendations
 * are badged "Recommended"; others are still fully selectable.
 *
 * Pre-live gate: each step has required conditions that must be met before a
 * collaborative session can start. These are enforced here with a clear
 * blocking message rather than a silent disabled state.
 */

import { useState } from 'react';
import { Check, Wrench, Radio, Clock, Play, CheckCircle2, RotateCcw, ChevronDown, ChevronUp, AlertTriangle, Copy, Link2 } from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';
import { REGISTRY_TOOL_SPECS_BY_ID, REGISTRY_SESSION_TOOL_IDS } from '../../../generated/registryToolSpecs';
import { recommendToolsForStep, stepModeAvailability } from '../engineLogic';
import type { InviteStepRunResult } from '../inviteStepRun';
import { useSessionEngineStore } from '../store';
import type { RunMode, SessionEvent, StepId } from '../types';
import { evaluateSituationRules } from '../../../lib/situationRecommendation';
import {
  matrixToolIdsToRegistryIds,
  registryIdToMatrixToolId,
  registryIdsToMatrixToolIds,
} from '../../../lib/situationToolIds';
import { recordMethodChoice } from '../../../lib/methodRecommendationOverrides';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { isToolSwitchedOff } from '../../../lib/switchedOffTools';
import {
  SituationBasisNote,
  SituationEscapeNotice,
  SituationToolNote,
} from '../../../components/prioritization/sessions/SituationRecommendationNotes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── Pre-live gate ────────────────────────────────────────────────────────────

interface GateResult {
  /** True if the step can start a live/open collaborative run. */
  allowed: boolean;
  /** Human-readable reason when blocked. */
  reason?: string;
  /** The step(s) the user needs to address to unblock. */
  fixAt?: StepId[];
}

/**
 * Returns the conditions that must be satisfied before a collaborative run of
 * `step` can start.
 *
 * Hard blocks come from one place — `stepModeAvailability` in engineLogic,
 * which holds the S1–S5 prerequisite matrix (owner ruling 2026-09-22):
 * collaborative anything needs participants; S4 needs items + criteria; S5
 * needs items, plus criteria and values only when the chosen tool scores
 * against them. Everything else here is either a tool-fit reality (you cannot
 * narrow an empty list, you cannot rank one item) or a soft suggestion that
 * says what would make the run better without standing in its way.
 */
function checkPreLiveGate(step: StepId, event: SessionEvent): GateResult {
  const itemCount = event.items.length;
  const hasCriteria = event.criteria.length > 0;

  // The prerequisite matrix — the only dependency-shaped hard block.
  const availability = stepModeAvailability(event, step, 'collaborative');
  if (!availability.available) {
    return { allowed: false, reason: availability.reason, fixAt: availability.fixAt };
  }

  switch (step) {
    case 'S1':
      if (!hasCriteria)
        return {
          allowed: true,
          reason: 'Criteria (S3) are optional here. Adding them first makes it clearer who qualifies to take part.',
          fixAt: ['S3', 'S4'],
        };
      return { allowed: true };

    case 'S2':
      // Narrowing a list you do not have is not a dependency — there is simply
      // nothing on screen to select from.
      if (itemCount === 0)
        return {
          allowed: false,
          reason: 'There are no items in the pool yet. S2 is about narrowing a list — add items first so there\'s something to select from.',
          fixAt: ['S2'],
        };
      if (!hasCriteria)
        return {
          allowed: true,
          reason: 'Criteria (S3) are optional here. With them, the group can say why an item stays in.',
          fixAt: ['S3'],
        };
      return { allowed: true };

    case 'S3':
      return { allowed: true };

    case 'S4':
      return { allowed: true };

    case 'S5':
      // Tool-fit, not a dependency: one item has nothing to be ranked against.
      if (itemCount < 2)
        return {
          allowed: false,
          reason: itemCount === 0
            ? 'Nothing to rank. Add items in S2 first.'
            : 'Need at least 2 items to run a ranking session. Add more items in S2.',
          fixAt: ['S2'],
        };
      if (!hasCriteria)
        return {
          allowed: true,
          reason: 'This tool does not need criteria, so the ranking will be unweighted. Add criteria (S3) and values (S4) if you want a weighted one.',
          fixAt: ['S3', 'S4'],
        };
      return { allowed: true };

    default:
      return { allowed: true };
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  step: StepId;
  itemCount: number;
}

export function ToolFraming({ step, itemCount }: Props) {
  const event = useSessionEngineStore((s) => s.event);
  const setStepGoal = useSessionEngineStore((s) => s.setStepGoal);
  const setStepTool = useSessionEngineStore((s) => s.setStepTool);
  const setStepRunMode = useSessionEngineStore((s) => s.setStepRunMode);
  const setStepRunStatus = useSessionEngineStore((s) => s.setStepRunStatus);
  const startStepRun = useSessionEngineStore((s) => s.startStepRun);
  const { showToast } = useToast();
  const [showAll, setShowAll] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteStepRunResult | null>(null);
  const [copiedJoin, setCopiedJoin] = useState(false);

  if (!event) return null;
  // Capture for nested async handlers — TS does not narrow store-selected `event`
  // across the async boundary inside launchRun.
  const liveEvent = event;

  const cfg = liveEvent.steps[step];
  const gate = checkPreLiveGate(step, liveEvent);
  const isSingleContainer = liveEvent.containerMode === 'single';
  const runSessionId =
    liveEvent.containerMode === 'multi'
      ? liveEvent.childSessions[step] ?? (liveEvent.id.startsWith('evt_') ? null : liveEvent.id)
      : liveEvent.id.startsWith('evt_')
        ? null
        : liveEvent.id;
  const joinPath = runSessionId ? `/prioritization/sessions/${runSessionId}/participate` : null;
  const rosterWithAccounts = liveEvent.participants.filter((p) => p.userId).length;
  const nameOnlyCount = liveEvent.participants.filter((p) => !p.userId).length;

  async function launchRun() {
    if (!gate.allowed || launching) return;
    if (liveEvent.persisting || liveEvent.id.startsWith('evt_')) {
      showToast(
        'error',
        'Still saving this event',
        'Wait a second for the session to finish saving, then click Start again.',
      );
      return;
    }
    if (cfg.mode !== 'collaborative') {
      showToast(
        'error',
        'Switch to Collaborative',
        'Declared mode records an answer without a live tool run. Use Collaborative to start live.',
      );
      return;
    }

    setLaunching(true);
    setInviteResult(null);
    try {
      const targetSessionId = await startStepRun(step);
      if (!targetSessionId) {
        // createSession stores the real deny reason (e.g. missing Facilitate
        // Sessions) on lastCreateError — surface that instead of a vague toast.
        const { useSessionStore } = await import('../../../stores/prioritization/sessionStore');
        const createError = useSessionStore.getState().lastCreateError;
        showToast(
          'error',
          'Could not start live session',
          createError ??
            'Check that you are signed in, this event is saved, and you have Facilitate Sessions permission.',
        );
        return;
      }
      const { useUserStore } = await import('../../../stores/prioritization/userStore');
      const facilitatorId = useUserStore.getState().userId;
      const recipientIds = Array.from(
        new Set(
          liveEvent.participants
            .map((p) => p.userId)
            .filter((id): id is string => Boolean(id) && id !== facilitatorId),
        ),
      );
      setInviteResult({
        sessionId: targetSessionId,
        recipientIds,
        nameOnlyCount: liveEvent.participants.filter((p) => !p.userId).length,
        joinPath: `/prioritization/sessions/${targetSessionId}/participate`,
      });
      // invited=1: LiveSessionPage skips duplicate Live-now bells (already sent in startStepRun)
      const liveUrl = `/prioritization/sessions/${targetSessionId}/live?startRanking=1&step=${step}&invited=1`;
      // Open without the noopener feature flag — Chrome returns null from
      // window.open when noopener is in features, which made this look like a
      // no-op (or forced a same-tab navigate). Sever opener after open instead.
      const popup = window.open(liveUrl, '_blank');
      if (popup) {
        try {
          popup.opener = null;
        } catch {
          /* ignore */
        }
      } else {
        // Popup blocked — same-tab so the click never silently fails.
        window.location.assign(liveUrl);
      }
    } finally {
      setLaunching(false);
    }
  }

  async function copyJoinLink() {
    if (!joinPath) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${joinPath}`);
      setCopiedJoin(true);
      window.setTimeout(() => setCopiedJoin(false), 2000);
    } catch {
      /* ignore */
    }
  }

  // Recommended tools for this step (with fit check)
  const recommended = recommendToolsForStep(step, itemCount, event.jobId);
  const recommendedIds = new Set(recommended.map((r) => r.toolId));

  // All session-compatible tools NOT in the recommended list
  const otherTools = REGISTRY_SESSION_TOOL_IDS.filter((id: string) => !recommendedIds.has(id) && !isToolSwitchedOff(id)).map((id: string) => {
    const spec = REGISTRY_TOOL_SPECS_BY_ID[id];
    const min = spec?.compatibility?.minItems ?? 0;
    const max = spec?.compatibility?.maxItems;
    const fits = itemCount >= min && (max == null || itemCount <= max);
    let fitNote: string | undefined;
    if (!fits) {
      if (itemCount < min) fitNote = `needs at least ${min} items`;
      else if (max != null) fitNote = `best for ${max} or fewer items`;
    }
    return {
      toolId: id,
      displayName: spec?.displayName ?? id,
      description: spec?.description ?? '',
      fitsItemCount: fits,
      fitNote,
      isRecommended: false,
    };
  });

  // Situation rules (advisory). Precedence: fit/availability (hard) > situation
  // rules > step defaults. They reorder within what is on offer and annotate;
  // they never add a tool the step could not run.
  const byToolId = new Map([...recommended, ...otherTools].map((o) => [o.toolId, o]));
  const situation = evaluateSituationRules({
    vector: liveEvent.situationVector,
    availableToolIds: registryIdsToMatrixToolIds([...byToolId.keys()]),
    defaultToolIds: registryIdsToMatrixToolIds(recommended.map((r) => r.toolId)),
  });
  const promotedRegistryIds = matrixToolIdsToRegistryIds(
    situation.annotations.filter((a) => a.kind === 'promoted').map((a) => a.toolId),
  );
  const leadIds = situation.applied
    ? [...new Set([...promotedRegistryIds, ...recommended.map((r) => r.toolId)])]
    : recommended.map((r) => r.toolId);
  const leadSet = new Set(leadIds);
  const allRecommended = leadIds
    .map((id) => byToolId.get(id))
    .filter((o): o is NonNullable<typeof o> => Boolean(o))
    .map((r) => ({ ...r, isRecommended: true }));
  const restTools = situation.applied
    ? matrixToolIdsToRegistryIds(situation.orderedToolIds)
        .filter((id) => !leadSet.has(id))
        .map((id) => byToolId.get(id))
        .filter((o): o is NonNullable<typeof o> => Boolean(o))
    : otherTools;

  const chooseTool = (toolId: string) => {
    const next = cfg.toolId === toolId ? undefined : toolId;
    setStepTool(step, next);
    if (!next || !situation.applied) return;
    const workspaceId = getCurrentWorkspaceId();
    const chosenToolId = registryIdToMatrixToolId(next);
    if (!workspaceId || !chosenToolId) return;
    void recordMethodChoice({
      workspaceId,
      sessionId: UUID_RE.test(liveEvent.id) ? liveEvent.id : null,
      chosenToolId,
      recommendation: situation,
      vector: liveEvent.situationVector,
    });
  };

  return (
    <div className="mt-6 space-y-4 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] p-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          Run collaboratively
        </span>
        <span className="rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          optional
        </span>
      </div>

      {/* Pre-live gate — hard block shown as error, soft warning shown as advisory */}
      {!gate.allowed && (
        <div className="flex items-start gap-3 border border-electric-mustard-200 bg-electric-mustard-50 dark:border-electric-mustard-700/50 dark:bg-electric-mustard-900/20 px-3 py-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-electric-mustard-700 dark:text-electric-mustard-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-electric-mustard-800 dark:text-electric-mustard-200">Can&apos;t start yet</p>
            <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">{gate.reason}</p>
            {gate.fixAt && gate.fixAt.length > 0 && (
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Fix in: {gate.fixAt.map((s) => <span key={s} className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">{s}</span>).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
              </p>
            )}
          </div>
        </div>
      )}
      {gate.allowed && gate.reason && (
        <div className="flex items-start gap-3 border border-slate-blue-200 bg-slate-blue-50 dark:border-slate-blue-700/40 dark:bg-slate-blue-900/10 px-3 py-2.5">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-blue-700 dark:text-slate-blue-400" />
          <div className="space-y-0.5">
            <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">{gate.reason}</p>
            {gate.fixAt && gate.fixAt.length > 0 && (
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Consider addressing: {gate.fixAt.map((s) => <span key={s} className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">{s}</span>).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Question 1: Goal */}
      <div>
        <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-blue-600 text-[10px] font-bold text-white">1</span>
          What is the goal of this run?
        </label>
        <input
          value={cfg.goal ?? ''}
          onChange={(e) => setStepGoal(step, e.target.value)}
          placeholder="e.g. narrow 80 items to under 25"
          className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-400 focus:outline-none focus:ring-1 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-700"
        />
      </div>

      {/* Question 2: Tool */}
      <div>
        <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-blue-600 text-[10px] font-bold text-white">2</span>
          Pick a tool
        </label>

        <SituationEscapeNotice recommendation={situation} />
        <SituationBasisNote recommendation={situation} />

        {/* Recommended tools */}
        {allRecommended.length > 0 && (
          <div className="mb-2">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {situation.applied ? 'Recommended for this step and this room' : 'Recommended for this step'}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {allRecommended.map((opt) => (
                <ToolCard
                  key={opt.toolId}
                  opt={opt}
                  selected={cfg.toolId === opt.toolId}
                  onSelect={() => chooseTool(opt.toolId)}
                  recommended
                  note={<SituationToolNote recommendation={situation} toolId={registryIdToMatrixToolId(opt.toolId)} />}
                />
              ))}
            </div>
          </div>
        )}

        {/* Show all toggle */}
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 flex items-center gap-1.5 text-xs font-medium text-slate-blue-700 dark:text-slate-blue-400 hover:text-slate-blue-800 dark:hover:text-slate-blue-300"
        >
          {showAll ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {showAll ? 'Hide other tools' : `Show all tools (${restTools.length} more)`}
        </button>

        {showAll && restTools.length > 0 && (
          <div className="mt-2">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">All other tools</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {restTools.map((opt) => (
                <ToolCard
                  key={opt.toolId}
                  opt={opt}
                  selected={cfg.toolId === opt.toolId}
                  onSelect={() => chooseTool(opt.toolId)}
                  recommended={false}
                  note={<SituationToolNote recommendation={situation} toolId={registryIdToMatrixToolId(opt.toolId)} />}
                />
              ))}
            </div>
          </div>
        )}

        {/* Show currently selected tool even if it's outside the recommended list */}
        {cfg.toolId && !leadSet.has(cfg.toolId) && !showAll && (
          <div className="mt-2 flex items-center gap-2 border border-slate-blue-200 bg-slate-blue-50 dark:border-slate-blue-700/50 dark:bg-slate-blue-900/20 px-3 py-2 text-sm">
            <Check size={14} className="shrink-0 text-slate-blue-700 dark:text-slate-blue-400" />
            <span className="flex-1 text-[var(--text-pri)] dark:text-[var(--text-pri)]">{REGISTRY_TOOL_SPECS_BY_ID[cfg.toolId]?.displayName ?? cfg.toolId}</span>
            <button type="button" onClick={() => setStepTool(step, undefined)} className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-luminous-coral-700 dark:hover:text-luminous-coral-400">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Question 3: Run it */}
      <div>
        <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-blue-600 text-[10px] font-bold text-white">3</span>
          Run it
        </label>

        {!cfg.toolId ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">Pick a tool above first.</p>
        ) : (
          <div className="space-y-3">
            {isSingleContainer ? (
              /* A single-session event is one sitting by definition, so there is
                 no async choice to make here — but the screen used to show a
                 lone locked chip and leave the reader to infer that from its
                 caption. Seven people scoring ninety values each is exactly the
                 case where someone needs to know an async run exists and where
                 to get it. */
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">How:</span>
                  <span className="inline-flex items-center gap-1.5 border border-slate-blue-500 bg-slate-blue-50 dark:bg-slate-blue-900/30 px-2.5 py-1.5 text-xs font-medium text-slate-blue-700 dark:text-slate-blue-300">
                    <Radio size={13} /> Live — everyone in one sitting
                  </span>
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  This is a single-session event, so it runs in one sitting. To let people
                  answer in their own time, run it as a multi-session event instead.
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">How:</span>
                {(['live', 'open'] as RunMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => setStepRunMode(step, m)}
                    disabled={cfg.runStatus !== 'not_started'}
                    className={[
                      'flex items-center gap-1.5  border px-2.5 py-1.5 text-xs font-medium transition',
                      cfg.runMode === m
                        ? 'border-slate-blue-500 bg-slate-blue-50 text-slate-blue-700 dark:bg-slate-blue-900/30 dark:text-slate-blue-300'
                        : 'border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:border-slate-blue-400',
                      cfg.runStatus !== 'not_started' ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    {m === 'live' ? <Radio size={13} /> : <Clock size={13} />}
                    {m === 'live' ? 'Live (everyone now)' : 'Open (async input)'}
                  </button>
                ))}
              </div>
            )}

            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {cfg.runMode === 'live'
                ? 'A synchronous run — facilitate it in one sitting and resolve the result.'
                : 'Stays open as a session collecting input over time; close it when ready.'}
            </p>

            {cfg.runStatus === 'not_started' && (
              <button
                onClick={() => void launchRun()}
                disabled={
                  !gate.allowed ||
                  launching ||
                  !!event.persisting ||
                  event.id.startsWith('evt_') ||
                  cfg.mode !== 'collaborative'
                }
                title={
                  event.persisting || event.id.startsWith('evt_')
                    ? 'Saving this event…'
                    : cfg.mode !== 'collaborative'
                      ? 'Switch to Collaborative to start a live run'
                      : gate.allowed
                        ? undefined
                        : gate.reason
                }
                className={[
                  'flex items-center gap-1.5  px-3 py-2 text-sm font-medium transition',
                  gate.allowed &&
                  !launching &&
                  !event.persisting &&
                  !event.id.startsWith('evt_') &&
                  cfg.mode === 'collaborative'
                    ? 'bg-slate-blue-600 text-white hover:bg-slate-blue-700'
                    : 'cursor-not-allowed bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-ter)] dark:text-[var(--text-ter)]',
                ].join(' ')}
              >
                <Play size={15} /> {launching ? 'Starting…' : cfg.runMode === 'live' ? 'Start live session' : 'Open for input'}
              </button>
            )}

            {cfg.runStatus === 'running' && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1.5 rounded-full bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300 px-2.5 py-1 text-xs font-medium">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-electric-mustard-600" />
                    {cfg.runMode === 'live' ? 'Live in progress' : 'Open — collecting input'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStepRunStatus(step, 'complete')}
                    className="border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
                  >
                    {cfg.runMode === 'live' ? 'Mark complete' : 'Close session'}
                  </button>
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Record the result in the panel above.</span>
                </div>

                <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-3 py-2.5 space-y-2">
                  <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    {inviteResult
                      ? inviteResult.recipientIds.length > 0
                        ? `Sent Live now + My Tasks to ${inviteResult.recipientIds.length} participant${inviteResult.recipientIds.length === 1 ? '' : 's'}.`
                        : 'Nobody was notified — add workspace users in Participants (S1), then restart the run.'
                      : rosterWithAccounts > 0
                        ? 'Participants with accounts get an in-app Live now notice and a My Tasks join item when you start the run.'
                        : 'No workspace users on the roster — add people in Participants (S1) from the workspace list so they get Live now + My Tasks.'}
                    {(inviteResult?.nameOnlyCount ?? nameOnlyCount) > 0 && (
                      <span className="block mt-1 text-electric-mustard-700 dark:text-electric-mustard-300">
                        {(inviteResult?.nameOnlyCount ?? nameOnlyCount)} name-only entr
                        {(inviteResult?.nameOnlyCount ?? nameOnlyCount) === 1 ? 'y' : 'ies'} can’t receive notifications — pick them from the workspace roster instead.
                      </span>
                    )}
                  </p>
                  {joinPath && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        <Link2 size={12} /> Join link
                      </span>
                      <code className="min-w-0 flex-1 truncate rounded bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-1 text-sm font-mono text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                        {joinPath}
                      </code>
                      <button
                        type="button"
                        onClick={() => void copyJoinLink()}
                        className="inline-flex items-center gap-1 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
                      >
                        <Copy size={12} /> {copiedJoin ? 'Copied' : 'Copy'}
                      </button>
                      <a
                        href={joinPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-slate-blue-700 dark:text-slate-blue-400 hover:underline"
                      >
                        Open participant view
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {cfg.runStatus === 'complete' && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 rounded-full bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300 px-2.5 py-1 text-xs font-medium">
                  <CheckCircle2 size={13} /> Run complete
                </span>
                <button
                  onClick={() => setStepRunStatus(step, 'running')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]"
                >
                  <RotateCcw size={12} /> Reopen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolCard({
  opt,
  selected,
  onSelect,
  recommended,
  note,
}: {
  opt: { toolId: string; displayName: string; description: string; fitsItemCount: boolean; fitNote?: string };
  selected: boolean;
  onSelect: () => void;
  recommended: boolean;
  /** Situation-rule annotation for this tool, when one applies. */
  note?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'flex flex-col gap-1  border p-3 text-left transition',
        selected
          ? 'border-slate-blue-500 bg-slate-blue-50 ring-1 ring-slate-blue-200 dark:bg-slate-blue-900/30 dark:ring-slate-blue-700'
          : 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-slate-blue-400',
      ].join(' ')}
    >
      <span className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          <Wrench size={13} className="shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          {opt.displayName}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {recommended && !selected && (
            <span className="rounded bg-slate-blue-100 text-slate-blue-700 dark:bg-slate-blue-900/50 dark:text-slate-blue-400 px-1.5 py-0.5 text-[10px] font-medium">rec</span>
          )}
          {selected && <Check size={14} className="text-slate-blue-700 dark:text-slate-blue-400" />}
        </span>
      </span>
      <span className="line-clamp-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{opt.description}</span>
      {!opt.fitsItemCount && opt.fitNote && (
        <span className="text-[11px] font-medium text-electric-mustard-700 dark:text-electric-mustard-400">{opt.fitNote}</span>
      )}
      {note}
    </button>
  );
}
